// @ai-generated 2026-07-10T00:00:00Z by matthias.gessinger@synqony.com using Claude Sonnet 5
import { useIdLookup } from '@Hooks/useIdLookup';
import { useStammdatenRepository } from '@Hooks/useRepository';
import { ServiceAvvRepository } from '@Repositories/Stammdaten';
import { isOk, type ListRequest } from '@synqo/frontend-component-library';
import { useCallback } from 'react';

const fullList = (pageSize: number): ListRequest => ({
    page_no: 0,
    page_size: pageSize,
    full_text_search: '',
    sort_columns: [],
});

/**
 * Formats a compact AVV code for display: "170107" → "17 01 07", "061305*" → "06 13 05*".
 */
export const formatAvvCode = (code: string): string => {
    const dangerous = code.endsWith('*');
    const digits = dangerous ? code.slice(0, -1) : code;
    const grouped = digits.match(/.{1,2}/g)?.join(' ') ?? digits;
    return dangerous ? `${grouped}*` : grouped;
};

export interface AvvCodes {
    /** Map from service id to its primary AVV code, formatted for display, e.g. "17 01 07". */
    codes: ReadonlyMap<string, string>;
    /** Fetches the codes of any not-yet-loaded service ids and merges them into `codes`. */
    ensureCodes: (serviceIds: string[]) => void;
}

/**
 * Fetches the primary AVV code per service via the service's `avv` link list.
 * Shared by the Positions and Summary steps.
 *
 * A service without an AVV code is remembered as an empty code, so it is asked for only once.
 *
 * TODO(DEVCLD-2200): one request per service — expose the AVV code directly on the
 * service list/linkable response so the wizard can drop this fan-out.
 */
export function useAvvCodes(): AvvCodes {
    const serviceAvv = useStammdatenRepository(ServiceAvvRepository);

    const loadCodes = useCallback(
        async (serviceIds: string[]) =>
            await Promise.all(
                serviceIds.map(async (id) => {
                    const result = await serviceAvv.getLinkedObjects(fullList(1), id);
                    const rawCode = isOk(result) ? result.data.items[0]?.code ?? '' : '';
                    return [id, rawCode ? formatAvvCode(rawCode) : ''] as const;
                }),
            ),
        [serviceAvv],
    );

    const { valuesById, request } = useIdLookup(loadCodes);

    return { codes: valuesById, ensureCodes: request };
}
